import type { UseQueryResult } from "@tanstack/react-query";
import { useTenantQuery } from "./factory";
import { useApiClient } from "../react/ApiProvider";
import { qk } from "../query-keys/qk";
import type { ApiError } from "../errors/ApiError";

/**
 * Tenant branding — response shape mirrors backend /tenants/me/branding.
 * CC-02 accepted: endpoint returns 200 + neutral payload on unknown tenant.
 *
 * Phase 2 D-04 surface: Sidebar header renders `displayName` + `logoUrl`;
 * `primaryColorHex` drives `setTenantPrimary()` at auth bootstrap.
 */
export interface TenantBranding {
  /** English name, falling back to the Arabic one — for callers that do not know the language. */
  displayName: string;
  nameEn: string;
  nameAr: string;
  /** The tenant's own logo, or null when it has none (the neutral mark is not a tenant logo). */
  logoUrl: string | null;
  /** The wide header logo, or null when the tenant has not uploaded one. */
  wideLogoUrl: string | null;
  /** The tenant's brand colour, or null when it has none — callers then use the theme default. */
  primaryColorHex: string | null;
  /** The employee app's public store pages, or null where there is none. */
  appStoreUrl: string | null;
  playStoreUrl: string | null;
}

/** The URL the backend returns in place of a logo when a tenant has not uploaded one. */
const NEUTRAL_LOGO_PATH = "/brand/linkhr-logo.svg";

/** Raw `/auth/tenant-branding` body → `TenantBranding`. */
export function toTenantBranding(data: Record<string, unknown>): TenantBranding {
  const nameEn = typeof data.nameEn === "string" ? data.nameEn : "";
  const nameAr = typeof data.nameAr === "string" ? data.nameAr : "";
  const logo = typeof data.logoUrl === "string" && data.logoUrl !== "" ? data.logoUrl : null;
  const wide =
    typeof data.wideLogoUrl === "string" && data.wideLogoUrl !== "" ? data.wideLogoUrl : null;
  const primaryColorHex =
    typeof data.primaryColor === "string" && data.primaryColor !== "" ? data.primaryColor : null;
  const url = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);
  return {
    displayName: nameEn || nameAr,
    nameEn,
    nameAr,
    logoUrl: logo === NEUTRAL_LOGO_PATH ? null : logo,
    wideLogoUrl: wide,
    primaryColorHex,
    appStoreUrl: url(data.appStoreUrl),
    playStoreUrl: url(data.playStoreUrl),
  };
}

/** The tenant's name in the reader's language, else the other one. */
export function brandingName(
  branding: Pick<TenantBranding, "nameEn" | "nameAr">,
  language: string,
): string {
  const ar = language.startsWith("ar");
  return (ar ? branding.nameAr : branding.nameEn) || (ar ? branding.nameEn : branding.nameAr);
}

/**
 * Hand-written typed hook for GET /api/v1/auth/tenant-branding.
 *
 * Backend contract (openapi.json, CC-02): `tenantSlug` is a REQUIRED query
 * parameter. Callers must pass the slug — typically via `resolveTenant()`
 * from `@linkhr/auth` upstream (e.g. AppShell reads hostname, each app's
 * `_auth.tsx` passes it down). Omitting the slug → backend 400
 * "Required parameter 'tenantSlug' is not present."
 *
 * This wrapper lives inside @linkhr/api-client (the ONLY legitimate fetch()
 * location per project conventions FOUND-03 + Phase 1 D-04 precedent). Once the
 * generated client catches up to the hand-typed path + query, replace the
 * body with the schema-bound `client.GET("/api/v1/auth/tenant-branding", {
 * params: { query: { tenantSlug } } })` call.
 *
 * Goes through the full middleware chain (auth, correlation, billing).
 *
 * When `enabled === false` OR `tenantSlug` is undefined/empty, the query
 * is disabled — caller is responsible for rendering a sensible fallback.
 *
 * Response: `BrandingResponse { nameAr, nameEn, logoUrl, primaryColor }`, mapped by
 * `toTenantBranding`. Callers pick the name for their language with `brandingName`.
 */
export interface UseBrandingQueryOpts {
  enabled?: boolean;
  /** Tenant slug — REQUIRED by the backend. Skip fetch when empty. */
  tenantSlug?: string;
}

export function useBrandingQuery(
  opts: UseBrandingQueryOpts = {},
): UseQueryResult<TenantBranding, ApiError> {
  const client = useApiClient();
  const slug = opts.tenantSlug ?? "";
  const enabled = (opts.enabled ?? true) && slug.length > 0;
  return useTenantQuery<TenantBranding>(client, {
    queryKey: qk.auth.branding(slug.length > 0 ? slug : "_me_"),
    enabled,
    queryFn: async (c) => {
      // Hand-rolled typed fetch through the middleware chain.
      // `c.GET` is openapi-fetch's method; the query object is passed via
      // the second-arg `params.query` payload.
      const res = await (
        c as unknown as {
          GET: (
            path: string,
            init?: { params?: { query?: Record<string, unknown> } },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).GET("/api/v1/auth/tenant-branding", {
        params: { query: { tenantSlug: slug } },
      });
      if (res.error) throw res.error as ApiError;
      const data = res.data as Record<string, unknown> | undefined;
      if (!data) throw new Error("Branding response empty");
      return toTenantBranding(data);
    },
  });
}
